import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import { config, missingProductionConfig, useSupabase } from './config.ts';
import { AppStore, setStore } from './data/store.ts';
import { LocalSource } from './data/localSource.ts';
import type { DataSource } from './data/source.ts';
import type { SupabaseSource } from './data/supabaseSource.ts';
import { SignInScreen } from './features/settings/SignInScreen.tsx';
import { registerServiceWorker } from './lib/sw-register.ts';
import './styles.css';

/** Supabase's client is only downloaded when the app is configured to use it. */
async function createSource(): Promise<DataSource> {
  if (!useSupabase) return new LocalSource(config.inboxAddress, true);
  const { SupabaseSource } = await import('./data/supabaseSource.ts');
  return new SupabaseSource(config.supabaseUrl, config.supabaseAnonKey, config.inboxAddress);
}

const sourcePromise = missingProductionConfig ? null : createSource();

/** Shown instead of sample data when the live site was built without its Supabase settings. */
function MissingConfig() {
  return (
    <div className="app">
      <div className="screen" style={{ justifyContent: 'center', minHeight: '100dvh' }}>
        <span className="eyebrow">Natangelo</span>
        <h1 className="h1 h1-sm">This version isn't set up</h1>
        <p className="muted">
          It was built without the address of your shared data, so it can't sign you in. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in the hosting settings, then redeploy.
        </p>
      </div>
    </div>
  );
}

function Root() {
  const [phase, setPhase] = useState<'checking' | 'signin' | 'app'>('checking');
  const [source, setSource] = useState<DataSource | null>(null);

  useEffect(() => {
    if (phase !== 'checking' || !sourcePromise) return;
    let cancelled = false;
    (async () => {
      const src = await sourcePromise;
      if (cancelled) return;
      setSource(src);
      const s = await src.session();
      if (cancelled) return;
      if (!s.signedIn) {
        setPhase('signin');
        return;
      }
      const store = new AppStore(src);
      setStore(store);
      // Don't wait for the server: the app shows the copy saved on this phone (or its
      // loading screen) right away, and updates when the load finishes.
      void store.init();
      if (!cancelled) setPhase('app');
    })();
    return () => {
      cancelled = true;
    };
  }, [phase]);

  if (!sourcePromise) return <MissingConfig />;
  if (phase === 'checking') return null;
  if (phase === 'signin' && source?.kind === 'supabase') {
    return (
      <div className="app">
        <SignInScreen source={source as SupabaseSource} onSignedIn={() => setPhase('checking')} />
      </div>
    );
  }
  return <App />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);

if (!config.isPreview) registerServiceWorker();
// iOS only shows :active (the pressed look) once the page listens for touches.
document.addEventListener('touchstart', () => undefined, { passive: true });
